import React, { useContext, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { RawMaterialRow, RawMaterialUse } from '../../../../../../services/types/Calculator';
import { DropDown, Button, ConfirmModal, Modal } from '../../../../../../shared/components';
import RawMaterialsDetails from './Details/RawMaterialsDetails';
import MainContext from '../../../../../App/MainProvider/context';
import { calculateRawMaterial } from '../../Utils/moduleCalculates';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  rawMaterialUse: RawMaterialUse;
  onUpdateRawMaterialUse: (updatedRawMaterialUse: RawMaterialUse) => void;
}

const RawMaterialUseSection: React.FC<Props> = ({ rawMaterialUse, onUpdateRawMaterialUse }) => {
  const { t } = useTranslation();

  const rawMaterialUseOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(rawMaterialUse.rawMaterials
      ? rawMaterialUse.rawMaterials.map((x) => ({
          id: x.id,
          text: x.name,
          value: x.id.toString(),
        }))
      : []),
  ];

  const { notify } = useContext(MainContext);
  const [selectedValueRawMaterialOption, setSelectedValue] = useState<number | null>(0);
  const [rawMaterialInputValue, setRawMaterialInputValue] = useState<string>('');
  const [rows, setRows] = useState<RawMaterialRow[]>(rawMaterialUse.rows || []);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [rowForDelete, setRowForDelete] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const isAddButtonDisabled =
    selectedValueRawMaterialOption === null ||
    selectedValueRawMaterialOption === 0 ||
    Number(rawMaterialInputValue) <= 0;

  const handleChange = (value: number) => {
    setSelectedValue(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setRawMaterialInputValue(value);
    }
  };

  const isDuplicateRawMaterial = (existingRows: RawMaterialRow[], rawMaterialId: number) => {
    const isDuplicate = existingRows.some((row) => row.rawMaterialId === rawMaterialId);

    if (isDuplicate) {
      notify(
        'Raw Material Use',
        'This Raw Material has already been added.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const handleAddRow = () => {
    if (selectedValueRawMaterialOption !== null && Number(rawMaterialInputValue) > 0) {
      const selectedRawMaterial = rawMaterialUse.rawMaterials.find(
        (x) => x.id === selectedValueRawMaterialOption
      );

      if (selectedRawMaterial) {
        if (isDuplicateRawMaterial(rows, selectedRawMaterial.id)) {
          return;
        }

        const calculatedAreaCleared = calculateRawMaterial(
          selectedRawMaterial.factor,
          Number(rawMaterialInputValue)
        );

        const newRow = {
          id: 0,
          rawMaterialId: selectedRawMaterial.id,
          rawMaterial: selectedRawMaterial.name,
          factor: selectedRawMaterial.factor,
          quantity: Number(rawMaterialInputValue),
          totalCarbon: calculatedAreaCleared,
        };

        setRows((prevRows) => {
          const updatedRows = [...prevRows, newRow];
          const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalCarbon, 0);
          onUpdateRawMaterialUse({
            ...rawMaterialUse,
            rows: updatedRows,
            subTotal: totalCarbonSum,
          });
          return updatedRows;
        });

        setRawMaterialInputValue('');
        setSelectedValue(0);
      }
    }
  };

  const confirmRemoveRow = () => {
    if (rowForDelete !== null) {
      setRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);
        const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalCarbon, 0);
        onUpdateRawMaterialUse({
          ...rawMaterialUse,
          rows: updatedRows,
          subTotal: updatedRows.length > 0 ? totalCarbonSum : null,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const handleRemoveRow = (index: number) => {
    setRowForDelete(index);
    setShowConfirmDelete(true);
  };

  const totalCarbonSum = rows.reduce((sum, row) => sum + row.totalCarbon, 0);

  return (
    <>
      <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
      <div className="form-row align-items-center calculator-forms table-wrapper-calc">
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Raw Material')}
          </label>
        </div>
        <div className="col-sm-3 d-flex align-items-center">
          <DropDown
            elements={rawMaterialUseOptions}
            value={selectedValueRawMaterialOption}
            onChange={handleChange}
            key="drop-landUseChange"
            className="dropdown-module-calculator"
          />
        </div>
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Quantity Used (tons)')}
          </label>
        </div>

        <div className="col-sm-2 d-flex align-items-center">
          <input
            title="Quantity Used (tons)"
            type="number"
            id="landUseInput"
            value={rawMaterialInputValue}
            onChange={handleInputChange}
            min="0"
            maxLength={10}
            max={99999999.99}
            onKeyDown={handleKeyDown}
            className="calculator-input"
          />
        </div>
        <div className="col-sm-3">
          <Button
            htmlId="addRowLandUse"
            className="btn btn-primary px-4 py-1 calculator-add-button"
            style={{ float: 'right' }}
            text={t('Add Row')}
            onClick={handleAddRow}
            disabled={isAddButtonDisabled}
          />
        </div>
      </div>

      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Raw Material</th>
              <th>Quantity Used (tons)</th>
              <th className="text-right">Subtotal</th>
              <th className="action-column text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id}>
                <td>{row.rawMaterial}</td>
                <td>
                  <NumberFormatterSinSeparador value={row.quantity} />
                </td>
                <td className="text-right">
                  <NumberFormatterSinSeparador value={row.totalCarbon} /> t CO<sub>2</sub>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`remove-row-rawMaterial-${index}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="trash-alt"
                    text=""
                    onClick={() => handleRemoveRow(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`tr-rawMateiallink-detail-${rawMaterialUse.moduleId}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="clipboard-list"
                    text=""
                    onClick={() => setShowDetailsModal(true)}
                  />
                  <UncontrolledTooltip
                    className="tooltip-toolkit"
                    placement="bottom"
                    target={`tr-rawMateiallink-detail-${rawMaterialUse.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Details')}
                  </UncontrolledTooltip>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {showConfirmDelete && rowForDelete !== null && (
        <ConfirmModal
          visible={showConfirmDelete}
          buttonText={t('Confirm')}
          setVisible={setShowConfirmDelete}
          action={confirmRemoveRow}
        >
          {t('confirmDeleteCalculateMessage')}
        </ConfirmModal>
      )}

      <Modal
        title={rawMaterialUse.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <RawMaterialsDetails rawMaterial={rawMaterialUse} />
        </div>
      </Modal>
    </>
  );
};

export default RawMaterialUseSection;
